import React, { useState } from "react";
import CountryCode from "models/CountryCode";
import ICountry from "./models/ICountry";
import CountryOption from "./CountryOption";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import ArrowDownwardRoundedIcon from "@material-ui/icons/ArrowDownwardRounded";
import Spacer from "components/Spacer";
import Typography from "@material-ui/core/Typography";
import ListDisplay from "components/listDisplay/ListDisplay";
import noop from "utils/noop";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        selectedCountryContainer: {
            display: "flex",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.common.black}`,
            cursor: "pointer",
        },
        iconArrow: {
            width: 20,
            height: 20,
            display: "flex",
            alignItems: "center",
            transition: `all 0.3s ease-in-out`,
            "& svg": {
                width: "100%",
            },
        },
        iconRotate: { transform: "rotate(180deg)" },
        countryListContainer: {
            padding: `${theme.spacing(1)}px 0px`,
        },
        labelText: {
            lineHeight: 1,
            [theme.breakpoints.down("xs")]: {
                fontSize: "0.8rem !important",
                "& p": {
                    fontSize: "0.8rem !important",
                },
            },
        },
        countryOption: {
            lineHeight: 2,
            "&:hover": {
                textDecoration: "underline",
                cursor: "pointer",
            },
        },
        displayListRoot: {
            justifyContent: "center",
        },
        displayListColumn: {
            padding: `0px ${theme.spacing(4)}px`,
        },
    })
);

export type ICountries = {
    [key in CountryCode]?: ICountry;
};

interface ICoutryListItem {
    code: CountryCode;
    country: ICountry;
}

interface IProps {
    countries: ICountries;
    defaultCountry: CountryCode;
    defaultShowOptions?: boolean;
    onSelectedCountryChange?: (selectedCountry: CountryCode) => void;
    onShowOptionsChange?: (showOptions: boolean) => void;
}

export default function useCountrySelect(props: IProps) {
    const classes = useStyles();
    const {
        countries,
        defaultCountry,
        defaultShowOptions = false,
        onSelectedCountryChange = noop,
        onShowOptionsChange = noop,
    } = props;
    const [_selectedCountryCode, _setSelectedCountryCode] = useState<CountryCode>(defaultCountry);
    const [_showOptions, _setShowOptions] = useState<boolean>(defaultShowOptions);

    const setShowOptions = (showOptions: boolean) => {
        _setShowOptions(showOptions);
        onShowOptionsChange(showOptions);
    };

    const onCountryClick = (countryCode: CountryCode) => {
        _setSelectedCountryCode(countryCode);
        onSelectedCountryChange(countryCode);
        setShowOptions(false);
    };

    const toggleSelect = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setShowOptions(!_showOptions);
    };

    let countryList: ICoutryListItem[] = [];
    for (const key in countries) {
        if (countries.hasOwnProperty(key)) {
            const coutryCode = key as CountryCode;
            const country: ICountry = countries[coutryCode] as ICountry;

            countryList.push({
                code: coutryCode,
                country: country,
            });
        }
    }

    return {
        countrySelectLabel: (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div className={classes.selectedCountryContainer} onClick={toggleSelect}>
                    {_showOptions ? (
                        <Typography className={classes.labelText} variant="body1">
                            Select Your Country
                        </Typography>
                    ) : (
                        <CountryOption
                            className={classes.labelText}
                            country={countries[_selectedCountryCode] as ICountry}
                        />
                    )}
                    <Spacer />
                    <div
                        className={`${classes.iconArrow} ${_showOptions ? classes.iconRotate : ""}`}
                    >
                        <ArrowDownwardRoundedIcon />
                    </div>
                </div>
            </div>
        ),
        countrySelectOptions: (
            <div className={classes.countryListContainer}>
                <ListDisplay
                    items={countryList}
                    classes={{
                        root: classes.displayListRoot,
                        column: classes.displayListColumn,
                    }}
                    renderItem={(item: ICoutryListItem) => (
                        <CountryOption
                            key={item.code}
                            country={item.country}
                            className={classes.countryOption}
                            onClick={() => onCountryClick(item.code)}
                        />
                    )}
                    breakpoints={[
                        { breakpoint: "xs", numberOfColums: 1 },
                        { breakpoint: "sm", numberOfColums: 2 },
                        { breakpoint: "md", numberOfColums: 2 },
                        { breakpoint: "lg", numberOfColums: 2 },
                        { breakpoint: "xl", numberOfColums: 2 },
                    ]}
                />
            </div>
        ),
    };
}
